import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import JSZip from 'jszip';
import calorieAIService from './calorieAI/calorieAIService';
import storageService from './storageService';

class BackupRestoreService {
  isNativePlatform = Capacitor.isNativePlatform();

  // 把 Blob 轉為 Base64 字串
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        // FileReader 產生的結果會包含 data URI 前綴，例如 data:application/zip;base64,....
        // 我們只需要 base64 內容
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async exportBackup() {
    if (!this.isNativePlatform) {
      throw new Error('此功能僅限原生 App (iOS/Android) 使用');
    }

    const zip = new JSZip();

    try {
      // 1. 備份 CalorieAI (Dexie)
      const calorieAIData = await calorieAIService.exportAllData();
      zip.file('calorieai_backup.json', calorieAIData);

      // 2. 備份 Vision Boards 及其圖片
      const visionBoards = await storageService.getItem('visionBoards.json', '[]');
      zip.file('visionBoards.json', visionBoards);
      
      try {
        const vbDir = await Filesystem.readdir({
          path: 'visionboards',
          directory: Directory.Data
        });
        
        if (vbDir && vbDir.files) {
          const vbFolder = zip.folder('visionboards');
          for (const fileInfo of vbDir.files) {
            // Capacitor 6 readdir returns array of FileInfo objects
            const fileName = fileInfo.name || fileInfo; 
            const fileData = await Filesystem.readFile({
              path: `visionboards/${fileName}`,
              directory: Directory.Data
            });
            // fileData.data 已經是 base64 string
            vbFolder.file(fileName, fileData.data, { base64: true });
          }
        }
      } catch (e) {
        console.log('No visionboards directory found or error reading it:', e);
      }

      // 3. 備份 LocalStorage (排除已廢棄的 CalorieAI 舊資料)
      const localStorageData = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // 忽略所有以 calorieai_ 開頭的舊版 localStorage 鍵，
        // 因為這些資料現在都已經由 Dexie (calorieai_backup.json) 管理了。
        // 這能避免把包含 base64 圖片的舊資料一起打包，造成檔案過大或 OOM。
        if (!key.startsWith('calorieai_')) {
          localStorageData[key] = localStorage.getItem(key);
        }
      }
      zip.file('localStorage_backup.json', JSON.stringify(localStorageData, null, 2));

      // 生成 ZIP Blob
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6 // 適度壓縮以減小體積
        }
      });

      // 轉為 Base64 (可能有 OOM 風險)
      const base64Data = await this.blobToBase64(zipBlob);

      // 寫入檔案系統 (使用 Cache 目錄避免堆積)
      const fileName = `SuperCloud_Backup_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.zip`;
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });

      // 使用 Share 插件分享/儲存檔案
      await Share.share({
        title: 'SuperCloud 備份檔',
        text: '請妥善保存您的 SuperCloud 備份檔',
        url: savedFile.uri,
        dialogTitle: '儲存或分享備份檔'
      });

      return true;
    } catch (error) {
      console.error('匯出備份失敗:', error);
      throw error;
    }
  }

  async importBackup(file) {
    if (!this.isNativePlatform) {
      throw new Error('此功能僅限原生 App (iOS/Android) 使用');
    }

    try {
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(file);

      // 檢查是否為有效的備份檔
      if (!zipContent.files['calorieai_backup.json'] && 
          !zipContent.files['visionBoards.json'] && 
          !zipContent.files['localStorage_backup.json']) {
        throw new Error('無效的備份檔案格式');
      }

      // 1. 還原 CalorieAI
      if (zipContent.files['calorieai_backup.json']) {
        const calorieData = await zipContent.files['calorieai_backup.json'].async('string');
        await calorieAIService.importData(calorieData);
      }

      // 2. 還原 Vision Boards 及其圖片
      if (zipContent.files['visionBoards.json']) {
        const visionData = await zipContent.files['visionBoards.json'].async('string');
        await storageService.setItem('visionBoards.json', visionData);
      }
      
      const vbFolder = zipContent.folder('visionboards');
      if (vbFolder) {
        // 先確保資料夾存在
        try {
          await Filesystem.mkdir({
            path: 'visionboards',
            directory: Directory.Data,
            recursive: true
          });
        } catch (e) {
          // 資料夾可能已存在
        }
        
        const filePromises = [];
        vbFolder.forEach((relativePath, file) => {
          if (!file.dir) {
            filePromises.push(async () => {
              const base64Data = await file.async('base64');
              await Filesystem.writeFile({
                path: file.name, // 包含資料夾名稱，例如 'visionboards/xxx.jpg'
                data: base64Data,
                directory: Directory.Data
              });
            });
          }
        });
        
        // 循序執行寫入以避免並發問題
        for (const writeTask of filePromises) {
          await writeTask();
        }
      }

      // 3. 還原 LocalStorage
      if (zipContent.files['localStorage_backup.json']) {
        const localDataString = await zipContent.files['localStorage_backup.json'].async('string');
        const localData = JSON.parse(localDataString);
        
        localStorage.clear();
        
        for (const [key, value] of Object.entries(localData)) {
          localStorage.setItem(key, value);
        }
      }

      return true;
    } catch (error) {
      console.error('匯入備份失敗:', error);
      throw error;
    }
  }
}

export const backupRestoreService = new BackupRestoreService();
export default backupRestoreService;
